import Decimal from 'decimal.js';

import { normalizeCurrency, rateKey } from '../../../util/exchangeRates';
import { BASE_CURRENCY } from '../../../shared/priceNormalization';

// The currency the `price` URL param and publicData.priceEUR are both expressed
// in. Re-exported so the filter components have a single place to read it from.
export { BASE_CURRENCY };

const isNumber = value => typeof value === 'number' && !Number.isNaN(value);

const resolveRate = (fromCurrency, toCurrency, exchangeRates = {}) => {
  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(toCurrency);

  if (!from || !to || from === to) {
    return 1;
  }

  const direct = exchangeRates[rateKey(from, to)]?.rate;
  if (typeof direct === 'number' && !Number.isNaN(direct)) {
    return direct;
  }

  const inverse = exchangeRates[rateKey(to, from)]?.rate;
  if (typeof inverse === 'number' && inverse !== 0) {
    return new Decimal(1).dividedBy(new Decimal(inverse)).toNumber();
  }

  return null;
};

export const isOriginalCurrencySelected = selectedCurrency => {
  const normalizedSelection = normalizeCurrency(selectedCurrency);
  return !normalizedSelection || normalizedSelection === 'ORIGINAL';
};

export const selectDisplayCurrency = (selectedCurrency, marketplaceCurrency, exchangeRates = {}) => {
  const normalizedSelection = normalizeCurrency(selectedCurrency);
  const normalizedMarketplace = normalizeCurrency(marketplaceCurrency);

  if (isOriginalCurrencySelected(selectedCurrency)) {
    return normalizedMarketplace;
  }

  if (normalizedSelection === normalizedMarketplace) {
    return normalizedMarketplace;
  }

  const rate = resolveRate(normalizedMarketplace, normalizedSelection, exchangeRates);
  return isNumber(rate) ? normalizedSelection : normalizedMarketplace;
};

export const convertPriceValue = (value, fromCurrency, toCurrency, exchangeRates = {}) => {
  if (!isNumber(value)) {
    return value;
  }

  const rate = resolveRate(fromCurrency, toCurrency, exchangeRates);
  if (!isNumber(rate)) {
    return value;
  }

  const converted = new Decimal(value).times(new Decimal(rate));
  return converted.toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
};

export const convertRange = (range, fromCurrency, toCurrency, exchangeRates = {}) => {
  if (!range) {
    return range;
  }

  const { minPrice, maxPrice } = range;
  return {
    minPrice: convertPriceValue(minPrice, fromCurrency, toCurrency, exchangeRates),
    maxPrice: convertPriceValue(maxPrice, fromCurrency, toCurrency, exchangeRates),
  };
};

const clampValue = (value, minLimit, maxLimit) => {
  if (!isNumber(value)) {
    return value;
  }

  let clamped = value;
  if (isNumber(minLimit)) {
    clamped = Math.max(minLimit, clamped);
  }
  if (isNumber(maxLimit)) {
    clamped = Math.min(maxLimit, clamped);
  }
  return clamped;
};

export const clampRangeToLimits = (range, minLimit, maxLimit) => {
  if (!range) {
    return range;
  }

  const minPrice = clampValue(range.minPrice, minLimit, maxLimit);
  const maxPrice = clampValue(range.maxPrice, minLimit, maxLimit);
  const safeMax = isNumber(maxPrice) && isNumber(minPrice) && maxPrice < minPrice ? minPrice : maxPrice;

  return { minPrice, maxPrice: safeMax };
};

export const convertAndClampRange = (
  range,
  fromCurrency,
  toCurrency,
  exchangeRates = {},
  minLimit,
  maxLimit
) => {
  const converted = convertRange(range, fromCurrency, toCurrency, exchangeRates);
  return clampRangeToLimits(converted, minLimit, maxLimit);
};

export const convertConfigValue = (value, fromCurrency, toCurrency, exchangeRates = {}) => {
  return convertPriceValue(value, fromCurrency, toCurrency, exchangeRates);
};

/**
 * Round a price up to the next full thousand, which is what the slider ceiling
 * uses. Floored at 1000 so a result set of only cheap listings still yields a
 * usable range instead of collapsing the slider.
 *
 * @param {number} value price in the display currency
 * @returns {number} the next full thousand, at least 1000
 */
export const ceilToThousand = value => {
  if (!isNumber(value) || value <= 0) {
    return 1000;
  }
  return Math.max(1000, Math.ceil(value / 1000) * 1000);
};

/**
 * Derive a slider step from the ceiling. A fixed step of 5 is unusable once the
 * range runs into the hundreds of thousands (NOK), so we aim for ~200 steps
 * across the range and snap to a clean 1/2/5 x 10^n value.
 *
 * @param {number} max slider ceiling in the display currency
 * @returns {number} step size, at least 1
 */
export const deriveSliderStep = max => {
  const target = isNumber(max) ? max / 200 : 0;
  if (target <= 1) {
    return 1;
  }

  const exponent = Math.floor(Math.log10(target));
  const magnitude = Math.pow(10, exponent);
  const base = target / magnitude;
  const mantissa = base < 1.5 ? 1 : base < 3.5 ? 2 : base < 7.5 ? 5 : 10;

  return Math.max(1, mantissa * magnitude);
};

/**
 * Slider ceiling for the current filter context: the most expensive matching
 * listing, converted into the display currency and rounded up to the next full
 * thousand.
 *
 * @param {Object} params
 * @param {number|null} params.maxPriceEUR most expensive matching listing, in whole euros
 * @param {string} params.displayCurrency currency the slider is shown in
 * @param {Object} params.exchangeRates rates from the currency duck
 * @param {number} params.fallbackMax configured ceiling, already in the display
 *   currency, used when no listing max is known (empty result set, listings not
 *   yet normalized, or a failed bounds query)
 * @returns {number} slider ceiling in the display currency
 */
export const resolveSliderMax = ({ maxPriceEUR, displayCurrency, exchangeRates, fallbackMax }) => {
  if (!isNumber(maxPriceEUR) || maxPriceEUR <= 0) {
    return fallbackMax;
  }

  const converted = convertPriceValue(maxPriceEUR, BASE_CURRENCY, displayCurrency, exchangeRates);
  return ceilToThousand(converted);
};
